import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
  useRef,
} from "react";
import { InputGroup } from "react-bootstrap";
import Select from "react-select";
import { ArrayHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { Cliente } from "models/ECF/Cliente";
import { ClienteManager } from "managers";
import ToolTip from "components/ToolTip/ToolTip";
import { GridView } from "components/GridView/GridView";
import IconButton from "components/IconButton/IconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import { AutoComplete, SvgComponent } from "components";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Simplebar = lazy(() => import("components/Simplebar/Simplebar"));
const ClienteEdit = lazy(() => import("views/Cliente/ClienteEdit"));
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import {
  buttonStyle,
  controlsProportion,
  initialWhere,
  initialWhereModal,
} from "..";
import "../Lookup.style.scss";

export const ClienteLookup = forwardRef(
  (
    {
      isDisabled,
      idSecList = [],
      isInput = false,
      isMultiselect = false,
      onChange = () => { },
      onMultiselectChange = () => { },
      initialValue = { label: "", value: "" },
      initialValueList = [],
      limpiar = false,
      required = false,
      loadOnShowModal = false,
      loadValue = false,
    },
    ref
  ) => {
    const isMounted = useRef(true);
    const [showCanvas, setShowCanvas] = useState(false);
    const [showClienteEditCanvas, setShowClienteEditCanvas] = useState(false);
    const [clienteListModal, setClienteListModal] = useState([]);
    const [whereModal, setWhereModal] = useState(initialWhereModal);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [selectedValue, setSelectedValue] = useState({
      label: "",
      value: "",
      documento: "",
      email: "",
      direccion: "",
    });
    const [multiSelectedValue, setMultiSelectedValue] = useState([]);

    const ESTATUS_LIST = [
      { value: "", label: "Todos" },
      ...(Cliente.ESTATUS_LIST || []),
    ];

    const TIPO_DOCUMENTO_LIST = [
      { value: "", label: "Todos" },
      ...(Cliente.TIPO_DOCUMENTO_LIST || []),
    ];

    const TIPO_COMPROBANTE_LIST = [
      { value: "", label: "Todos" },
      ...(Cliente.TIPO_COMPROBANTE_LIST || []),
    ];

    const { handleException } = useExceptionHandler();
    const { setPaginationValuesWhere } = usePaginationValuesWhere();

    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (limpiar) {
        handleMultiselectChange([]);
      }
    }, [limpiar]);

    useEffect(() => {
      if (!loadOnShowModal) {
        buscar();
      }
    }, [whereModal]);

    useEffect(() => {
      if (
        initialValue &&
        initialValue.value &&
        initialValue.value !== selectedValue.value
      ) {
        if (loadValue && !initialValue.label) buscarPorId(initialValue.value);
        else setSelectedValue(initialValue);
      }
    }, [initialValue.value]);

    useEffect(() => {
      if (initialValueList && initialValueList.length > 0) {
        setMultiSelectedValue(initialValueList);
      }
    }, [initialValueList.length]);

    const handleMultiselectChange = (value) => {
      setMultiSelectedValue(value ? value : []);
      onMultiselectChange(value ? value : []);
    };

    const handleSelectChange = (value) => {
      setSelectedValue(value ? value : {});
      onChange(value ? value : {});
    };

    function buscar() {
      const whereWithPaginationValues = {
        Estatus: Cliente.DEFAULT_ESTATUS_VALUE,
        ...whereModal,
        ...pagination,
        IdSecList: idSecList,
      };
      ClienteManager
        .listado(whereWithPaginationValues)
        .then((res) => {
          if (isMounted.current) {
            setPaginationValuesWhere(res.Headers, pagination, setPagination);
            setClienteListModal(res.Data);
          }
        })
        .catch((err) => handleException(err));
    }

    const buscarPorId = (idsec) => {
      ClienteManager
        .buscarPorId({ IdSec: idsec, EsLookup: true })
        .then((res) => {
          setSelectedValue({ label: res.label, value: res.value });
        })
        .catch((err) => handleException(err));
    };

    const handleCerrar = () => {
      setShowCanvas(false);
      setWhereModal(initialWhereModal);
    };

    const onPageIndexChanging = (pageNumber) => {
      setPagination((prevstate) => {
        return { ...prevstate, IndiceDePagina: pageNumber - 1 };
      });
      setWhereModal({ ...whereModal });
    };

    const getSuggestions = async (value) => {
      const inputValue = value.trim().toLowerCase();
      const res = await ClienteManager
        .listado({
          ...initialWhere,
          RazonSocial: inputValue,
          IdSecList: idSecList,
          EsLookup: true,
        });
      return res.Data;
    };

    const addToList = (newItem) => {
      setMultiSelectedValue((prev) => (prev ? [...prev, newItem] : [newItem]));
    };

    useImperativeHandle(ref, () => ({
      addToList,
      openClienteEdit: () => setShowClienteEditCanvas(true),
    }));

    const renderAutoComplete = () => (
      <AutoComplete
        required={required}
        isDisabled={isDisabled}
        getList={getSuggestions}
        noBorderRadius={isInput}
        isMultiselect={isMultiselect}
        placeholder={isMultiselect ? "Clientes" : "Cliente"}
        parentValue={isMultiselect ? multiSelectedValue : selectedValue}
        setParentValue={
          isMultiselect ? handleMultiselectChange : handleSelectChange
        }
      />
    );

    return (
      <>
        {!isInput ? (
          <InputGroup className="w-100">
            <div style={{ width: controlsProportion.input }}>
              {renderAutoComplete()}
            </div>
            {!isDisabled && (
              <InputGroup.Append style={{ width: controlsProportion.button }}>
                <AnimatedIconButton
                  size="40px"
                  height="18"
                  width="18"
                  message="Seleccionar Cliente"
                  style={buttonStyle}
                  disabled={isDisabled}
                  iconName="search"
                  onClick={() => {
                    setShowCanvas(true);
                    setTimeout(() => buscar(), 200);
                  }}
                />
              </InputGroup.Append>
            )}
          </InputGroup>
        ) : (
          <>
            <div style={{ width: controlsProportion.inputGroup }}>
              {renderAutoComplete()}
            </div>
            {!isDisabled && (
              <InputGroup.Append style={{ width: controlsProportion.button }}>
                <AnimatedIconButton
                  size="40px"
                  height="18"
                  width="18"
                  message="Seleccionar Cliente"
                  style={buttonStyle}
                  disabled={isDisabled}
                  iconName="search"
                  onClick={() => {
                    setShowCanvas(true);
                    setTimeout(() => buscar(), 200);
                  }}
                />
              </InputGroup.Append>
            )}
          </>
        )}

        {showClienteEditCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <ClienteEdit
              IdSec={undefined}
              showCanvas={showClienteEditCanvas}
              setShowCanvas={setShowClienteEditCanvas}
              showLoading={() => {}}
              hideLoading={() => {}}
              updateList={buscar}
              onCreated={(res) => {
                handleSelectChange({
                  label: res.RazonSocial,
                  value: res.IdSec,
                  RazonSocial: res.RazonSocial,
                  TipoDocumento: res.TipoDocumento,
                  Documento: res.Documento,
                  TipoComprobante: res.Tipo,
                });
              }}
            />
          </Suspense>
        )}

        {showCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <Simplebar
              backdrop={true}
              placement={"end"}
              portal={false}
              size="xl"
              useLoading={true}
              visible={showCanvas}
              setShow={(e) => setShowCanvas(e)}
              title="Seleccionar Cliente"
              btnLeft={{ show: false, func: () => { } }}
              btnRight={{ show: false, func: () => { } }}
            >
              <GridView
                pagination={pagination}
                setPagination={setPagination}
                allowPaging={pagination.EsPaginable}
                pageSize={pagination.CantidadPorPagina}
                pageIndex={pagination.IndiceDePagina}
                lastPageNumber={pagination.PaginasTotal}
                dataSource={clienteListModal}
                onPageIndexChanging={onPageIndexChanging}
                where={whereModal}
                setWhere={setWhereModal}
              >
                <GridView.Column
                  isSticky
                  bodyRenderer={(row) => (
                    <div className="float-right">
                      <IconButton
                        onClick={() => {
                          let values = multiSelectedValue || [];
                          if (isMultiselect) {
                            handleMultiselectChange(
                              ArrayHelper.removeObjectsDuplicated(
                                [
                                  ...values,
                                  {
                                    label: row.RazonSocial,
                                    value: row.IdSec,
                                    RazonSocial: row.RazonSocial,
                                    TipoDocumento: row.TipoDocumento,
                                    Documento: row.Documento,
                                    TipoComprobante: row.Tipo
                                  },
                                ],
                                ["value", "label"]
                              )
                            );
                          } else {
                            handleSelectChange({
                              label: row.RazonSocial,
                              value: row.IdSec,
                              RazonSocial: row.RazonSocial,
                              TipoDocumento: row.TipoDocumento,
                              Documento: row.Documento,
                              TipoComprobante: row.Tipo
                            });
                          }
                          handleCerrar();
                        }}
                      >
                        <ToolTip message="Seleccionar">
                          <div>
                            <SvgComponent height="20" width="20" iconName="check" />
                          </div>
                        </ToolTip>
                      </IconButton>
                    </div>
                  )}
                />
                <GridView.Column
                  headerText="Id"
                  dataField="IdSec"
                  sortExpression="IdSec"
                  width="110px"
                />
                <GridView.Column
                  headerText="Tipo Documento"
                  dataField="TipoDocumentoDesc"
                  sortExpression="TipoDocumentoDesc"
                  width="150px"
                  filterRenderer={() => (
                    <Select
                      className="react-select primary"
                      options={TIPO_DOCUMENTO_LIST}
                      value={TIPO_DOCUMENTO_LIST.filter(
                        (opt) => opt.value === (whereModal.TipoDocumento || TIPO_DOCUMENTO_LIST[0].value)
                      )}
                      name="TipoDocumento"
                      onChange={(e) =>
                        setWhereModal({ ...whereModal, TipoDocumento: e.value })
                      }
                    />
                  )}
                />
                <GridView.Column
                  headerText="Documento"
                  dataField="Documento"
                  sortExpression="Documento"
                  width="200px"
                />
                <GridView.Column
                  headerText="Razon Social"
                  dataField="RazonSocial"
                  sortExpression="RazonSocial"
                  width="250px"
                />
                <GridView.Column
                  headerText="Tipo Comprobante"
                  dataField="TipoDesc"
                  sortExpression="TipoDesc"
                  width="180px"
                  filterRenderer={() => (
                    <Select
                      className="react-select primary"
                      options={TIPO_COMPROBANTE_LIST}
                      value={TIPO_COMPROBANTE_LIST.filter(
                        (opt) => opt.value === (whereModal.Tipo || TIPO_COMPROBANTE_LIST[0].value)
                      )}
                      name="Tipo"
                      onChange={(e) =>
                        setWhereModal({ ...whereModal, Tipo: e.value })
                      }
                    />
                  )}
                />
              </GridView>
            </Simplebar>
          </Suspense>
        )}
      </>
    );
  }
);